import React, { useEffect, useState } from 'react';
import { Newspaper, ExternalLink, Clock, RefreshCw, TrendingUp, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function DailyNewsPage() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/news/latest`);
      const data = await response.json();
      
      if (data.success) {
        setNews(data.articles || []);
        setLastUpdated(new Date());
      } else {
        setError(data.error || 'Failed to load news');
      }
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to load news. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', label: 'All News' },
    { id: 'markets', label: 'Markets' },
    { id: 'companies', label: 'Companies' },
    { id: 'economy', label: 'Economy' },
    { id: 'commodities', label: 'Commodities' }
  ];

  const filteredNews = selectedCategory === 'all' 
    ? news 
    : news.filter(item => item.category?.toLowerCase() === selectedCategory);

  const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
      case 'markets': return 'bg-blue-500/20 text-blue-400';
      case 'companies': return 'bg-purple-500/20 text-purple-400';
      case 'economy': return 'bg-green-500/20 text-green-400';
      case 'commodities': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getSentimentIcon = (sentiment) => {
    if (sentiment === 'positive') return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (sentiment === 'negative') return <TrendingUp className="w-4 h-4 text-red-500 rotate-180" />;
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading market news...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-900/20 to-background border-b border-blue-500/20 px-4 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-2xl font-bold font-display flex items-center gap-2">
                <Newspaper className="w-7 h-7 text-blue-500" />
                Daily News
              </h1>
              <p className="text-sm text-muted-foreground">Latest news impacting ASX markets</p>
            </div>
            <button 
              onClick={fetchNews}
              className="p-2 rounded-full bg-card-hover hover:bg-primary/20 transition-colors"
              title="Refresh News"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mt-4">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card-hover text-muted-foreground hover:text-foreground'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
            <span>{filteredNews.length} articles</span>
            {lastUpdated && (
              <>
                <span>•</span>
                <span>Updated: {lastUpdated.toLocaleTimeString()}</span>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Error State */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
            <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-500">{error}</p>
            <button 
              onClick={fetchNews}
              className="mt-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* News List */}
      <div className="px-4 py-4">
        <div className="max-w-7xl mx-auto space-y-4">
          {filteredNews.length === 0 && !error ? (
            <div className="text-center py-12">
              <Newspaper className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No News Available</h3>
              <p className="text-sm text-muted-foreground">
                Check back later for the latest market news
              </p>
            </div>
          ) : (
            filteredNews.map((article, index) => (
              <article
                key={article.id || index}
                className="stock-card hover:border-primary/30 transition-colors"
                data-testid={`news-${index}`}
              >
                <div className="flex gap-4">
                  {/* Thumbnail */}
                  {article.image && (
                    <div className="hidden sm:block w-32 h-24 flex-shrink-0">
                      <img 
                        src={article.image} 
                        alt="" 
                        className="w-full h-full object-cover rounded-lg"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        {article.category && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryColor(article.category)}`}>
                            {article.category}
                          </span>
                        )}
                        {article.sentiment && getSentimentIcon(article.sentiment)}
                        {article.isBreaking && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 animate-pulse">
                            Breaking
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground flex items-center gap-1 flex-shrink-0">
                        <Clock className="w-3 h-3" />
                        {article.publishedAt ? formatDistanceToNow(new Date(article.publishedAt), { addSuffix: true }) : 'Recently'}
                      </span>
                    </div>

                    <h3 className="text-lg font-semibold mb-2 line-clamp-2">
                      {article.title}
                    </h3>

                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {article.summary || article.description}
                    </p>

                    {/* Related Stocks */}
                    {article.relatedStocks && article.relatedStocks.length > 0 && (
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs text-muted-foreground">Related:</span>
                        {article.relatedStocks.map(stock => (
                          <span key={stock} className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
                            {stock}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        {article.source}
                      </span>
                      {article.url && (
                        <a
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline flex items-center gap-1"
                        >
                          Read more
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
      </div>

      {/* Quick Links to News Sources */}
      <div className="px-4 py-6 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-sm font-semibold text-muted-foreground mb-3">Quick Links to ASX News Sources</h3>
          <div className="flex flex-wrap gap-3">
            <a 
              href="https://www.smh.com.au/business/markets" 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-4 py-2 bg-card-hover rounded-lg text-sm hover:bg-primary/20 transition-colors flex items-center gap-2"
            >
              SMH Markets
              <ExternalLink className="w-3 h-3" />
            </a>
            <a 
              href="https://www.afr.com/markets" 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-4 py-2 bg-card-hover rounded-lg text-sm hover:bg-primary/20 transition-colors flex items-center gap-2"
            >
              AFR Markets
              <ExternalLink className="w-3 h-3" />
            </a>
            <a 
              href="https://www.asx.com.au/markets/market-resources/announcements" 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-4 py-2 bg-card-hover rounded-lg text-sm hover:bg-primary/20 transition-colors flex items-center gap-2"
            >
              ASX Announcements
              <ExternalLink className="w-3 h-3" />
            </a>
            <a 
              href="https://www.reuters.com/markets/asia/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-4 py-2 bg-card-hover rounded-lg text-sm hover:bg-primary/20 transition-colors flex items-center gap-2"
            >
              Reuters Asia
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DailyNewsPage;
